import React, { useMemo, useState } from "react";
import axios from "axios";
import { Check, Link2, Loader2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGES = {
  nation: "N",
  youtube: "YT",
  facebook: "f",
  instagram: "IG",
  tiktok: "TT",
  x: "X",
  custom: "+",
};

function providerState(provider) {
  if (!provider) return { label: "Not connected", tone: "text-slate-400" };
  if (provider.connected && provider.live_capable === false) {
    return {
      label: provider.live_message || "Connected · LIVE approval pending",
      tone: "text-amber-300",
    };
  }
  if (provider.connected) return { label: "Connected", tone: "text-green-300" };
  if (provider.ready) return { label: provider.action || "Connect account", tone: "text-[#9dc1ff]" };
  return { label: provider.setup_message || "Setup pending", tone: "text-slate-500" };
}

export default function CommunicationsDestinationPicker({
  destinations = [],
  providers = [],
  selectedIds = [],
  maxDestinations,
  onChange,
  onConnected,
  showHeading = true,
}) {
  const [connecting, setConnecting] = useState(null);

  const rows = useMemo(() => {
    const actual = [...destinations];
    const represented = new Set(actual.map((item) => item.provider));
    const synthetic = providers
      .filter((provider) => !represented.has(provider.provider))
      .map((provider) => ({
        id: `provider:${provider.provider}`,
        provider: provider.provider,
        name: provider.label,
        account_label: providerState(provider).label,
        synthetic: true,
        provider_meta: provider,
      }));
    return [...actual, ...synthetic];
  }, [destinations, providers]);

  const toggle = (destination) => {
    if (destination.synthetic) return;
    const id = destination.id;
    if (selectedIds.includes(id)) {
      onChange?.(selectedIds.filter((value) => value !== id));
      return;
    }
    if (!Number.isFinite(Number(maxDestinations))) {
      toast.error("Your Studio destination entitlement could not be loaded. Refresh and try again.");
      return;
    }
    if (selectedIds.length >= Number(maxDestinations)) {
      toast.error(`Your Studio plan allows up to ${maxDestinations} simultaneous destinations.`);
      return;
    }
    onChange?.([...selectedIds, id]);
  };

  const connect = async (provider) => {
    if (!provider?.ready) {
      toast.error(provider?.setup_message || `${provider?.label || "This platform"} is not ready to connect yet.`);
      return;
    }
    setConnecting(provider.provider);
    try {
      const response = await axios.get(`${API}/communications/social/connect/${provider.provider}`);
      const authorizationUrl = response.data?.authorization_url;
      if (!authorizationUrl) throw new Error("No authorization page was returned");
      sessionStorage.setItem("communications_return_to_create", "1");
      window.location.href = authorizationUrl;
      await onConnected?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || "Could not start account connection");
      setConnecting(null);
    }
  };

  return (
    <div>
      {showHeading && (
        <div className="mb-3">
          <div className="font-semibold">Select destinations</div>
          <p className="text-xs text-slate-500 mt-1">
            Choose where this specific live will go. If an account is not connected yet, connect it here.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        {rows.map((destination) => {
          const provider = destination.provider_meta || providers.find((item) => item.provider === destination.provider);
          const selected = !destination.synthetic && selectedIds.includes(destination.id);
          const nationReview = destination.id === "nation_live" && destination.approval_required;
          const pendingLive = destination.synthetic && provider?.connected && provider?.live_capable === false;
          const connectable = destination.synthetic && !provider?.connected && provider?.ready;
          const setupPending = destination.synthetic && !provider?.ready;

          return (
            <div
              key={destination.id}
              className={`rounded-xl border p-3 transition-colors ${
                selected
                  ? "border-[#8bb6ff] bg-[#183c73]/50"
                  : "border-slate-700 bg-[#0b0d11]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="h-9 min-w-9 px-2 rounded-lg bg-slate-800 flex items-center justify-center text-xs font-bold">
                  {BADGES[destination.provider] || "LIVE"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {destination.name}
                    {destination.id === "nation_live" && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${nationReview ? "bg-amber-500/15 text-amber-300" : "bg-green-500/15 text-green-300"}`}>
                        {nationReview ? "Approval required" : "Direct"}
                      </span>
                    )}
                  </div>
                  <div className={`text-xs mt-1 truncate ${pendingLive ? "text-amber-300" : "text-slate-400"}`}>
                    {destination.account_label || providerState(provider).label}
                  </div>
                </div>
                {!destination.synthetic && (
                  <button
                    type="button"
                    onClick={() => toggle(destination)}
                    className={`h-8 px-3 rounded-lg border text-xs font-semibold ${selected ? "border-[#8bb6ff] bg-[#8bb6ff] text-slate-950" : "border-slate-600 hover:border-[#8bb6ff] text-slate-200"}`}
                  >
                    {selected ? <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Selected</span> : "Select"}
                  </button>
                )}
                {connectable && (
                  <button
                    type="button"
                    disabled={connecting === provider.provider}
                    onClick={() => connect(provider)}
                    className="h-8 px-3 rounded-lg border border-[#8bb6ff] text-[#9dc1ff] text-xs font-semibold hover:bg-[#183c73]/40"
                  >
                    {connecting === provider.provider ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Connect</span>}
                  </button>
                )}
                {(pendingLive || setupPending) && (
                  <LockKeyhole className="w-4 h-4 text-slate-500 flex-shrink-0" />
                )}
              </div>
              {setupPending && provider?.setup_message && (
                <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{provider.setup_message}</p>
              )}
              {pendingLive && provider?.live_message && (
                <p className="text-[11px] text-amber-300/80 mt-2 leading-relaxed">{provider.live_message}</p>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-xs text-slate-500 mt-2">{selectedIds.length} / {Number.isFinite(Number(maxDestinations)) ? maxDestinations : "—"} destinations selected</div>
    </div>
  );
}
